import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Ban, CheckCircle2, History, ScrollText, DatabaseBackup, Loader2 } from "lucide-react";

export const BlockToggle = ({ u, onDone }) => {
  const { API } = useAuth();
  const toggle = async () => {
    if (!u.is_blocked && !window.confirm(`Bloquer le compte ${u.email} ? Il ne pourra plus se connecter ; ses contributions sont conservées.`)) return;
    try { await axios.put(`${API}/users/${u.id}/block`, { blocked: !u.is_blocked }); toast.success(u.is_blocked ? "Compte réactivé" : "Compte bloqué"); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <Button variant="outline" size="sm" onClick={toggle} title={u.is_blocked ? "Réactiver" : "Bloquer"}
      className={u.is_blocked ? "border-[#2C4230] text-[#2C4230]" : "border-red-700 text-red-700"} data-testid={`block-user-${u.id}`}>
      {u.is_blocked ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
    </Button>
  );
};

export const LoginsButton = ({ u }) => {
  const { API } = useAuth();
  const [items, setItems] = useState(null);
  const open = async () => { try { setItems((await axios.get(`${API}/users/${u.id}/logins`)).data); } catch { toast.error("Erreur"); } };
  return (
    <>
      <Button variant="ghost" size="sm" onClick={open} title="Dernières connexions" data-testid={`logins-user-${u.id}`}><History className="w-4 h-4" /></Button>
      <Dialog open={!!items} onOpenChange={o => !o && setItems(null)}>
        <DialogContent className="bg-[#FAF7F2]">
          <DialogHeader><DialogTitle className="font-serif-heritage text-2xl">Connexions de {u.name}</DialogTitle></DialogHeader>
          <ul className="text-sm space-y-1 max-h-80 overflow-y-auto" data-testid="user-logins-list">
            {(items || []).map(l => <li key={l.id} className={l.success ? "" : "text-red-700"}>{l.at?.slice(0, 16).replace("T", " ")} — {l.success ? "réussie" : `échouée (${l.reason})`} · {l.ip}</li>)}
            {items && !items.length && <li className="italic text-[#6E5B4F]">Aucune connexion enregistrée.</li>}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
};

const LABELS = {
  validate_member: "Validation d'un membre", revoke_member: "Révocation", password_changed: "Changement de mot de passe",
  password_reset: "Réinitialisation de mot de passe", block_member: "Blocage d'un compte", unblock_member: "Réactivation d'un compte",
};

export const AdminJournal = () => {
  const { API } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/activity`, { params: { admin_only: true, limit: 50 } }).then(r => setItems(r.data)).catch(() => {}); }, [API]);
  return (
    <section className="mt-12" data-testid="admin-journal">
      <h2 className="font-serif-heritage text-2xl text-[#993D22] mb-3 flex items-center gap-2"><ScrollText className="w-5 h-5" />Journal administratif</h2>
      <ul className="heritage-card p-4 text-sm space-y-1 max-h-96 overflow-y-auto">
        {items.map((a, i) => (
          <li key={a.id || i}>{a.at?.slice(0, 16).replace("T", " ")} — <span className="font-semibold">{a.user_name}</span> : {LABELS[a.action] || (a.action?.startsWith("role_change") ? `Changement de rôle (${a.action.split(":")[1]})` : a.action)} — {a.person_name}</li>
        ))}
        {!items.length && <li className="italic text-[#6E5B4F]">Aucun événement.</li>}
      </ul>
    </section>
  );
};

export const BackupButton = ({ onDone }) => {
  const { API } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/backup`, {}, { timeout: 600000 });
      setResult(data); toast.success("Sauvegarde créée — téléchargement en cours"); onDone?.();
      const res = await axios.get(`${API}/admin/downloads/${encodeURIComponent(data.name)}`, { responseType: "blob", timeout: 600000 });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data]));
      link.download = data.name;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) { toast.error(err.response?.data?.detail || "Échec de la sauvegarde"); } finally { setBusy(false); }
  };
  return (
    <div className="heritage-card p-4 mb-4" data-testid="backup-panel">
      <Button className="btn-terra" onClick={run} disabled={busy} data-testid="create-backup-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DatabaseBackup className="w-4 h-4 mr-2" />}
        {busy ? "Sauvegarde en cours…" : "Créer une sauvegarde maintenant"}
      </Button>
      <p className="text-xs text-[#6E5B4F] mt-2">Copie complète en lecture seule : fiches, filiations, unions, familles, chefs, sources, historique, comptes (sans mots de passe) et photos/documents. Aucune donnée n'est modifiée. Téléchargez le fichier aussitôt créé.</p>
      {result && (
        <div className="text-sm mt-3 text-[#2C4230]" data-testid="backup-result">
          {result.name} — {(result.size / 1048576).toFixed(1)} Mo · {result.counts?.persons ?? 0} fiches · {result.counts?.users ?? 0} comptes · {result.counts?.chiefs ?? 0} chefs · {result.media_files} fichiers médias
          {result.media_missing?.length > 0 && <span className="text-red-700"> · {result.media_missing.length} fichier(s) introuvable(s)</span>}
        </div>
      )}
    </div>
  );
};
